import type {
  Grid,
  GridPosition,
  Payline,
  ReelStops,
  ReelSymbolId,
  RegularSymbolId,
} from '../core/types';
import {
  MAX_REPLAY_STEPS,
  REPLAY_SCHEMA_VERSION,
  type FreeSpinReplayV2,
  type LegacyReplayLineDecoder,
  type ReplayLineWinV2,
  type ReplayPotOfGreedV2,
  type ReplayPresentationMode,
  type ReplayDraftV2,
  type ReplayStepV2,
} from './schema';
import type { EgyptianGodEvent, PersistedStickyWild } from '../bonus/types';

const BET_CENTS = new Set([10, 20, 50, 100, 200, 500, 600, 700, 800, 900, 1_000, 2_000]);
const REEL_SYMBOLS = new Set<ReelSymbolId>([
  'BEWD', 'DM', 'JINZO', 'TW', 'ACE', 'KING', 'QUEEN', 'JACK', 'WILD', 'SCATTER',
]);
const PAYING_SYMBOLS = new Set<RegularSymbolId | 'WILD'>([
  'BEWD', 'DM', 'JINZO', 'TW', 'ACE', 'KING', 'QUEEN', 'JACK', 'WILD',
]);

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as UnknownRecord
    : null;
}

function indexed(value: unknown): readonly unknown[] | null {
  if (Array.isArray(value)) {
    const result: unknown[] = [];
    value.forEach((entry: unknown) => result.push(entry));
    return result;
  }
  const source = record(value);
  if (!source) return null;
  const keys = Object.keys(source).filter((key) => /^\d+$/.test(key)).sort((a, b) => Number(a) - Number(b));
  if (keys.length === 0 || keys.some((key, index) => Number(key) !== index)) return null;
  return keys.map((key) => source[key]);
}

function safeInteger(value: unknown, min = 0, max = Number.MAX_SAFE_INTEGER): number | null {
  return Number.isSafeInteger(value) && Number(value) >= min && Number(value) <= max ? Number(value) : null;
}

function finiteNumber(value: unknown, min = 0): number | null {
  const result = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(result) && result >= min ? result : null;
}

function text(value: unknown, maxLength: number): string | null {
  if (typeof value !== 'string') return null;
  const result = value.replace(/\s+/g, ' ').trim().slice(0, maxLength);
  return result || null;
}

function id(value: unknown, fallback?: string): string | null {
  const result = text(value, 128) ?? text(fallback, 128);
  return result && /^[A-Za-z0-9_-]+$/.test(result) ? result : null;
}

function presentationMode(value: unknown): ReplayPresentationMode {
  if (value === 'performance' || value === 'classic') return 'performance';
  return 'cards';
}

interface DecodedGrid {
  readonly grid: Grid;
  readonly materializedLegacyPog: boolean;
}

function decodeGrid(value: unknown, legacyPogPosition?: GridPosition): DecodedGrid | null {
  const columns = indexed(value);
  if (!columns || columns.length !== 5) return null;
  const result: ReelSymbolId[][] = [];
  let materializedLegacyPog = false;
  for (const [reel, rawColumn] of columns.entries()) {
    const column = indexed(rawColumn);
    if (!column || column.length !== 3) return null;
    const decodedColumn: ReelSymbolId[] = [];
    for (const [row, symbol] of column.entries()) {
      if (REEL_SYMBOLS.has(symbol as ReelSymbolId)) {
        decodedColumn.push(symbol as ReelSymbolId);
        continue;
      }
      if (symbol !== 'POG' || legacyPogPosition?.reel !== reel || legacyPogPosition.row !== row) return null;
      // V1 sometimes overwrote the source id. JACK is display-safe; lines are marked incomplete below.
      decodedColumn.push('JACK');
      materializedLegacyPog = true;
    }
    result.push(decodedColumn);
  }
  return { grid: result as unknown as Grid, materializedLegacyPog };
}

function grid(value: unknown): Grid | null {
  return decodeGrid(value)?.grid ?? null;
}

function stops(value: unknown): ReelStops | null {
  const values = indexed(value);
  if (!values || values.length !== 5) return null;
  const result = values.map((stop) => safeInteger(stop, 0, 57));
  return result.some((stop) => stop === null) ? null : result as unknown as ReelStops;
}

function position(value: unknown): GridPosition | null {
  const source = record(value);
  if (!source) return null;
  const reel = safeInteger(source.reel, 0, 4);
  const row = safeInteger(source.row, 0, 2);
  return reel === null || row === null ? null : { reel, row } as GridPosition;
}

function positions(value: unknown): readonly PersistedStickyWild[] | null {
  if (value === undefined || value === null) return [];
  const values = indexed(value);
  if (!values || values.length > 15) return null;
  const unique = new Map<string, PersistedStickyWild>();
  for (const rawPosition of values) {
    const decoded = position(rawPosition);
    if (!decoded) return null;
    const source = record(rawPosition);
    const multiplier = source?.multiplier === undefined ? undefined : safeInteger(source.multiplier, 1);
    if (multiplier === null) return null;
    unique.set(`${decoded.reel}:${decoded.row}`, {
      ...decoded, ...(multiplier === undefined ? {} : { multiplier }),
    });
  }
  return [...unique.values()].sort((left, right) => left.reel - right.reel || left.row - right.row);
}

function godEvents(value: unknown): readonly EgyptianGodEvent[] | undefined | null {
  if (value === undefined || value === null) return undefined;
  const values = indexed(value);
  if (!values || values.length > 3) return null;
  const result: EgyptianGodEvent[] = [];
  for (const candidate of values) {
    const source = record(candidate);
    if (!source || !['RA', 'SLIFER', 'OBELISK'].includes(String(source.god))) return null;
    const order = safeInteger(source.order, 1, 3);
    if (order === null) return null;
    result.push(candidate as EgyptianGodEvent);
  }
  return Object.freeze(result.sort((left, right) => left.order - right.order));
}

function lineWin(value: unknown): ReplayLineWinV2 | null {
  const source = record(value);
  if (!source) return null;
  const lineIndex = safeInteger(source.lineIndex, 0, 9);
  const count = safeInteger(source.count, 3, 5);
  const winCents = safeInteger(source.winCents, 0);
  const rawRoute = indexed(source.route);
  const symbolId = source.symbolId as RegularSymbolId | 'WILD';
  if (lineIndex === null || count === null || winCents === null || !PAYING_SYMBOLS.has(symbolId)) return null;
  if (!rawRoute || rawRoute.length !== 5 || rawRoute.some((row) => safeInteger(row, 0, 2) === null)) return null;
  return { lineIndex, count: count as 3 | 4 | 5, winCents, symbolId, route: rawRoute as Payline };
}

function lineWins(value: unknown): readonly ReplayLineWinV2[] | null {
  if (value === undefined || value === null) return [];
  const values = indexed(value);
  if (!values || values.length > 10) return null;
  const result = values.map(lineWin);
  if (result.some((entry) => entry === null)) return null;
  const decoded = result as ReplayLineWinV2[];
  return new Set(decoded.map((entry) => entry.lineIndex)).size === decoded.length ? decoded : null;
}

function pot(value: unknown): ReplayPotOfGreedV2 | undefined | null {
  if (value === undefined || value === null) return undefined;
  const source = record(value);
  if (!source) return null;
  const decodedPosition = position(source.position ?? source);
  const awarded = safeInteger(source.awardedFreeSpins, 1, 1);
  if (!decodedPosition || awarded !== 1 || source.transformedToWild !== true) return null;
  return { position: decodedPosition, awardedFreeSpins: 1, transformedToWild: true };
}

function difference(after: readonly GridPosition[], before: readonly GridPosition[]): readonly GridPosition[] {
  const beforeKeys = new Set(before.map((item) => `${item.reel}:${item.row}`));
  return after.filter((item) => !beforeKeys.has(`${item.reel}:${item.row}`));
}

function decodeV2Step(value: unknown, expectedIndex: number, previousCumulative: number): ReplayStepV2 | null {
  const source = record(value);
  if (!source) return null;
  const decodedStops = stops(source.stops);
  const decodedGrid = grid(source.grid);
  const decodedLines = lineWins(source.lineWins);
  const spinIndex = safeInteger(source.spinIndex, 1, MAX_REPLAY_STEPS);
  const spinWinCents = safeInteger(source.spinWinCents, 0);
  const cumulativeWinCents = safeInteger(source.cumulativeWinCents, 0);
  const scatterCount = safeInteger(source.scatterCount, 0, 5);
  const retriggerAwarded = safeInteger(source.retriggerAwarded, 0, 12);
  const freeSpinsLeftAfter = safeInteger(source.freeSpinsLeftAfter, 0, MAX_REPLAY_STEPS);
  const freeSpinsTotalAfter = safeInteger(source.freeSpinsTotalAfter, 1, MAX_REPLAY_STEPS);
  const stickyBefore = positions(source.stickyBefore);
  const stickyAfter = positions(source.stickyAfter);
  const newlySticky = positions(source.newlySticky);
  const decodedPot = pot(source.potOfGreed);
  const decodedGodEvents = godEvents(source.godEvents);
  if (!decodedStops || !decodedGrid || !decodedLines || !stickyBefore || !stickyAfter || !newlySticky || decodedPot === null || decodedGodEvents === null) return null;
  if (spinIndex !== expectedIndex || spinWinCents === null || cumulativeWinCents === null || scatterCount === null
    || retriggerAwarded === null || freeSpinsLeftAfter === null || freeSpinsTotalAfter === null) return null;
  if (cumulativeWinCents !== previousCumulative + spinWinCents) return null;
  if (decodedLines.reduce((sum, entry) => sum + entry.winCents, 0) !== spinWinCents) return null;
  const potKey = decodedPot === undefined
    ? null
    : `${decodedPot.position.reel}:${decodedPot.position.row}`;
  const expectedNewlySticky = difference(stickyAfter, stickyBefore)
    .filter((item) => `${item.reel}:${item.row}` !== potKey)
    .filter((item) => !decodedGodEvents?.some((event) => event.sliferTargets?.some((target) => (
      target.previousMultiplier === 0 && target.reel === item.reel && target.row === item.row
    ))));
  if (JSON.stringify(newlySticky) !== JSON.stringify(expectedNewlySticky)) return null;
  return {
    spinIndex, stops: decodedStops, grid: decodedGrid, lineWins: decodedLines, spinWinCents,
    cumulativeWinCents, scatterCount, retriggerAwarded, freeSpinsLeftAfter, freeSpinsTotalAfter,
    stickyBefore, stickyAfter, newlySticky,
    ...(decodedPot ? { potOfGreed: decodedPot } : {}),
    ...(decodedGodEvents ? { godEvents: decodedGodEvents } : {}),
  };
}

interface DecodedLegacyStep {
  readonly step: ReplayStepV2;
  readonly linesComplete: boolean;
}

function decodeLegacyStep(
  value: unknown,
  expectedIndex: number,
  previousCumulative: number,
  betCents: number,
  decoder?: LegacyReplayLineDecoder,
): DecodedLegacyStep | null {
  const source = record(value);
  if (!source) return null;
  const decodedStops = stops(source.stops);
  const decodedPot = pot(source.pogEffect);
  if (decodedPot === null) return null;
  const legacyGrid = decodeGrid(source.grid, decodedPot?.position);
  const decodedGrid = legacyGrid?.grid ?? null;
  const spinWin = finiteNumber(source.spinWin);
  const scatterCount = safeInteger(source.scatterCount, 0, 5);
  const freeSpinsLeftAfter = safeInteger(source.freeSpinsLeftAfter, 0, MAX_REPLAY_STEPS);
  const freeSpinsTotalAfter = safeInteger(source.freeSpinsTotalAfter, 1, MAX_REPLAY_STEPS);
  const stickyBefore = positions(source.stickyBefore);
  const stickyAfter = positions(source.stickyAfter);
  if (!decodedStops || !decodedGrid || spinWin === null || scatterCount === null || freeSpinsLeftAfter === null
    || freeSpinsTotalAfter === null || !stickyBefore || !stickyAfter || decodedPot === null) return null;
  const spinWinCents = Math.round(spinWin * 100);
  const decodedLines = legacyGrid?.materializedLegacyPog
    ? null
    : decoder?.decodeStoredResult({
      grid: decodedGrid,
      betCents,
      spinWinCents,
      stickyPositions: stickyBefore,
    }) ?? null;
  const retriggerAwarded = source.retriggerAwarded === true && scatterCount >= 3
    ? 10 + Math.min(2, scatterCount - 3)
    : 0;
  const step: ReplayStepV2 = {
    spinIndex: expectedIndex, stops: decodedStops, grid: decodedGrid, lineWins: decodedLines ?? [],
    spinWinCents, cumulativeWinCents: previousCumulative + spinWinCents, scatterCount,
    retriggerAwarded, freeSpinsLeftAfter, freeSpinsTotalAfter, stickyBefore, stickyAfter,
    newlySticky: difference(stickyAfter, stickyBefore),
    ...(decodedPot ? { potOfGreed: decodedPot } : {}),
  };
  return { step, linesComplete: decodedLines !== null };
}

export function decodeReplayV2(value: unknown): FreeSpinReplayV2 | null {
  const source = record(value);
  if (!source || source.version !== REPLAY_SCHEMA_VERSION) return null;
  const replayId = id(source.clientEntryId ?? source.id);
  const name = text(source.name, 20);
  const betCents = safeInteger(source.betCents, 1, 2_000);
  const totalWinCents = safeInteger(source.totalWinCents, 0);
  const createdAt = safeInteger(source.createdAt, 1);
  const rawSteps = indexed(source.steps);
  if (!replayId || !name || betCents === null || !BET_CENTS.has(betCents) || totalWinCents === null || createdAt === null
    || typeof source.linesComplete !== 'boolean'
    || !rawSteps || rawSteps.length === 0 || rawSteps.length > MAX_REPLAY_STEPS) return null;
  const steps: ReplayStepV2[] = [];
  for (const [index, rawStep] of rawSteps.entries()) {
    const decoded = decodeV2Step(rawStep, index + 1, steps.at(-1)?.cumulativeWinCents ?? 0);
    if (!decoded) return null;
    steps.push(decoded);
  }
  const totalSpins = safeInteger(source.totalSpins, steps.length, MAX_REPLAY_STEPS);
  const totalAwarded = safeInteger(source.totalAwarded, steps.length, MAX_REPLAY_STEPS);
  const retriggerCount = safeInteger(source.retriggerCount, 0, MAX_REPLAY_STEPS);
  const finalStickyCount = safeInteger(source.finalStickyCount, 0, 15);
  if (totalSpins === null || totalAwarded === null || retriggerCount === null || finalStickyCount === null
    || totalSpins !== steps.length || totalWinCents !== steps.at(-1)?.cumulativeWinCents
    || finalStickyCount !== steps.at(-1)?.stickyAfter.length) return null;
  const ownerUid = text(source.ownerUid, 128) ?? undefined;
  return {
    version: 2, id: replayId, clientEntryId: replayId, name, betCents, totalWinCents, createdAt,
    presentationMode: presentationMode(source.presentationMode), totalSpins, totalAwarded,
    retriggerCount, finalStickyCount, steps, linesComplete: source.linesComplete,
    ...(ownerUid ? { ownerUid } : {}),
  };
}

export function decodeLegacyReplayV1(
  value: unknown,
  fallbackId?: string,
  lineDecoder?: LegacyReplayLineDecoder,
): FreeSpinReplayV2 | null {
  const source = record(value);
  if (!source || (source.version !== undefined && Number(source.version) !== 1)) return null;
  const replayId = id(source.clientEntryId ?? source.id, fallbackId);
  const name = text(source.username ?? source.name, 20);
  const bet = finiteNumber(source.bet, 0.1);
  const createdAtNumber = finiteNumber(source.timestamp ?? source.createdAt, 1);
  const rawSteps = indexed(source.steps);
  if (!replayId || !name || bet === null || createdAtNumber === null || !rawSteps || rawSteps.length === 0
    || rawSteps.length > MAX_REPLAY_STEPS) return null;
  const betCents = Math.round(bet * 100);
  if (!BET_CENTS.has(betCents)) return null;
  const steps: ReplayStepV2[] = [];
  let linesComplete = lineDecoder !== undefined;
  for (const [index, rawStep] of rawSteps.entries()) {
    const decoded = decodeLegacyStep(rawStep, index + 1, steps.at(-1)?.cumulativeWinCents ?? 0, betCents, lineDecoder);
    if (!decoded) return null;
    steps.push(decoded.step);
    linesComplete &&= decoded.linesComplete;
  }
  const totalAwarded = safeInteger(source.totalAwarded, steps.length, MAX_REPLAY_STEPS) ?? steps.at(-1)!.freeSpinsTotalAfter;
  const retriggerCount = safeInteger(source.retriggerCount, 0, MAX_REPLAY_STEPS)
    ?? steps.filter((step) => step.retriggerAwarded > 0).length;
  const finalStickyCount = steps.at(-1)!.stickyAfter.length;
  return {
    version: 2, id: replayId, clientEntryId: replayId, name, betCents,
    totalWinCents: steps.at(-1)!.cumulativeWinCents, createdAt: Math.round(createdAtNumber),
    presentationMode: presentationMode(source.visualMode), totalSpins: steps.length, totalAwarded,
    retriggerCount, finalStickyCount, steps, legacySource: true,
    linesComplete,
  };
}

export function decodeReplay(
  value: unknown,
  fallbackId?: string,
  lineDecoder?: LegacyReplayLineDecoder,
): FreeSpinReplayV2 | null {
  return decodeReplayV2(value) ?? decodeLegacyReplayV1(value, fallbackId, lineDecoder);
}

export function decodeReplayDraftV2(value: unknown): ReplayDraftV2 | null {
  const source = record(value);
  if (!source || source.version !== REPLAY_SCHEMA_VERSION) return null;
  const draftId = id(source.id);
  const name = text(source.name, 20);
  const betCents = safeInteger(source.betCents, 1, 2_000);
  const startedAt = safeInteger(source.startedAt, 1);
  const totalAwarded = safeInteger(source.totalAwarded, 1, MAX_REPLAY_STEPS);
  const retriggerCount = safeInteger(source.retriggerCount, 0, MAX_REPLAY_STEPS);
  const rawSteps = indexed(source.steps);
  if (!draftId || !name || betCents === null || !BET_CENTS.has(betCents) || startedAt === null
    || totalAwarded === null || retriggerCount === null || !rawSteps || rawSteps.length > MAX_REPLAY_STEPS) return null;
  const steps: ReplayStepV2[] = [];
  for (const [index, rawStep] of rawSteps.entries()) {
    const decoded = decodeV2Step(rawStep, index + 1, steps.at(-1)?.cumulativeWinCents ?? 0);
    if (!decoded) return null;
    steps.push(decoded);
  }
  if (steps.length > totalAwarded) return null;
  return {
    version: 2, id: draftId, name, betCents, startedAt,
    presentationMode: presentationMode(source.presentationMode), totalAwarded, retriggerCount, steps,
  };
}
